import { observer } from 'mobx-react-lite';
import { ReactElement, useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useLoader } from '../../../helpers/useLoader';
import { InputStatus } from '../../../models/error.model';
import { DatasetDetailsHeader } from '../components/DatasetDetailsHeader';
import { DatasetDetails } from '../DatasetDetails.context';
import { Datasets } from '../datasets.context';

export const DatasetDetailsHeaderContainer = observer((): ReactElement => {
  const { isLoading, withLoaderAsync } = useLoader();
  const detailsContext = useContext(DatasetDetails);
  const datasetsContext = useContext(Datasets);

  const { datasetId } = useParams<{ datasetId: string }>();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [author, setAuthor] = useState('');
  const [termsOfUse, setTermsOfUse] = useState('');
  const [nameStatus, setNameStatus] = useState(InputStatus.empty());
  const [descriptionStatus, setDescriptionStatus] = useState(InputStatus.empty());
  const [authorStatus, setAuthorStatus] = useState(InputStatus.empty());
  const [termsOfUseStatus, setTermsOfUseStatus] = useState(InputStatus.empty());

  useEffect(() => {
    const setup = async (): Promise<void> => {
      await withLoaderAsync(async () => detailsContext.service.getDatasetDetailsAsync(datasetId));
      const dataset = detailsContext.service.store.details;
      setName(dataset.name);
      setDescription(dataset.description);
      setAuthor(dataset.author);
      setTermsOfUse(dataset.termsOfUse);
      setNameStatus(InputStatus.valid());
      setDescriptionStatus(InputStatus.valid());
      setAuthorStatus(InputStatus.valid());
      setTermsOfUseStatus(InputStatus.valid());
    };
    void setup();
  }, []);

  const handleNameChange = (name: string): void => {
    setName(name);
    setNameStatus(InputStatus.empty());
  };

  const handleNameBlurAsync = async (): Promise<void> => {
    const nameStatus = InputStatus.buildFrom(await detailsContext.service.validateDatasetNameAsync(name, datasetId));

    if (nameStatus.isValid && name !== detailsContext.service.store.details.name) {
      datasetsContext.service.markStale();
      await datasetUpdateAsync();
      datasetsContext.store.refreshFromDetails(detailsContext.store.details);
    }

    setNameStatus(nameStatus);
  };

  const handleDescriptionChange = (description: string): void => {
    setDescription(description);
    setDescriptionStatus(InputStatus.empty());
  };

  const handleTermsOfUseChange = (termsOfUse: string): void => {
    setTermsOfUse(termsOfUse);
    setTermsOfUseStatus(InputStatus.empty());
  };

  const handleAuthorChange = (author: string): void => {
    setAuthor(author);
    setAuthorStatus(InputStatus.empty());
  };

  const handleDescriptionBlurAsync = async (): Promise<void> => {
    if (description !== detailsContext.service.store.details.description) {
      datasetsContext.service.markStale();
      await datasetUpdateAsync();
      datasetsContext.store.refreshFromDetails(detailsContext.store.details);
    }
    if (descriptionStatus.isEmpty) {
      setDescriptionStatus(InputStatus.valid());
    }
  };

  const handleTermsOfUseBlurAsync = async (): Promise<void> => {
    if (termsOfUse !== detailsContext.service.store.details.termsOfUse) {
      datasetsContext.service.markStale();
      await datasetUpdateAsync();
      datasetsContext.store.refreshFromDetails(detailsContext.store.details);
    }
    if (termsOfUseStatus.isEmpty) {
      setTermsOfUseStatus(InputStatus.valid());
    }
  };

  const handleAuthorBlurAsync = async (): Promise<void> => {
    if (author !== detailsContext.service.store.details.author) {
      datasetsContext.service.markStale();
      await datasetUpdateAsync();
      datasetsContext.store.refreshFromDetails(detailsContext.store.details);
    }
    if (authorStatus.isEmpty) {
      setAuthorStatus(InputStatus.valid());
    }
  };

  const datasetUpdateAsync = async (): Promise<void> => {
    await detailsContext.service.updateDatasetDetailsAsync({
      name,
      description,
      datasetId,
      author,
      termsOfUse,
    });
  };

  return (
    <>
      <DatasetDetailsHeader
        name={name}
        description={description}
        onNameChange={handleNameChange}
        onDescriptionChange={handleDescriptionChange}
        onNameBlur={handleNameBlurAsync}
        onDescriptionBlur={handleDescriptionBlurAsync}
        nameMaxLength={200}
        descriptionMaxLength={500}
        nameStatus={nameStatus}
        descriptionStatus={descriptionStatus}
        isLoading={isLoading}
        termsOfUse={termsOfUse}
        author={author}
        termsOfUseStatus={termsOfUseStatus}
        authorStatus={authorStatus}
        onAuthorBlur={handleAuthorBlurAsync}
        onTermsOfUseBlur={handleTermsOfUseBlurAsync}
        onAuthorChange={handleAuthorChange}
        onTermsOfUseChange={handleTermsOfUseChange}
      />
    </>
  );
});
